import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';
import { ShippingInfoCard, EMPTY_SHIPPING, cleanShipping } from '../components/ShippingInfoCard';
import { ChargeLibrary, chargeToLineItem } from '../components/ChargeLibrary';
import { RateCardLibrary } from '../components/RateCardLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EditInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    po_number: '',
    line_items: [],
    payment_terms: 'COD - Cash on Delivery',
    due_date: '',
    invoice_date: '',
    notes: '',
    amount_paid: 0,
    balance_due: 0
  });
  const [documentNumber, setDocumentNumber] = useState('');
  const [gstRate, setGstRate] = useState(0);
  const [shipping, setShipping] = useState(EMPTY_SHIPPING);
  const [coveredDOs, setCoveredDOs] = useState([]);

  useEffect(() => {
    fetchInvoice();
    axios.get(`${BACKEND_URL}/api/invoices/${id}/covered-dos`)
      .then((r) => setCoveredDOs(r.data.delivery_orders || []))
      .catch(() => setCoveredDOs([]));
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoices/${id}`);
      const invoice = response.data;
      setDocumentNumber(invoice.document_number || '');
      setFormData({
        customer_name: invoice.customer_name || '',
        customer_email: invoice.customer_email || '',
        customer_phone: invoice.customer_phone || '',
        customer_address: invoice.customer_address || '',
        po_number: invoice.po_number || '',
        line_items: invoice.line_items || [],
        payment_terms: invoice.payment_terms || 'COD - Cash on Delivery',
        due_date: invoice.due_date || '',
        invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : (invoice.created_at ? invoice.created_at.split('T')[0] : ''),
        notes: invoice.notes || '',
        amount_paid: invoice.amount_paid || 0,
        balance_due: invoice.balance_due || 0
      });
      setGstRate(invoice.gst_rate || 0);
      setShipping({ ...EMPTY_SHIPPING, ...(invoice.shipping_info || {}) });
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/admin/billing');
    } finally {
      setLoading(false);
    }
  };

  const addChargeFromLibrary = (c) => {
    const base = formData.line_items.reduce((s, i) => s + (i.amount || 0), 0);
    setFormData({ ...formData, line_items: [...formData.line_items, chargeToLineItem(c, base)] });
  };

  const addRateFromCard = (item) => {
    setFormData({ ...formData, line_items: [...formData.line_items, { ...item, lorry_no: '', uom: '', do_number: '' }] });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        { description: '', quantity: 1, hours: 0, unit_price: 0, amount: 0 }
      ]
    });
  };

  const removeLineItem = (index) => {
    const newItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newItems });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price' || field === 'hours') {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const hours = parseFloat(newItems[index].hours) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].amount = hours > 0 ? qty * hours * price : qty * price;
      newItems[index].amount_overridden = false;
    }
    if (field === 'amount') {
      newItems[index].amount_overridden = true;
    }
    
    setFormData({ ...formData, line_items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const gst = +(subtotal * (gstRate || 0) / 100).toFixed(2);
    const total = subtotal + gst;
    return { subtotal, gst, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const recalcItems = formData.line_items.map(item => {
        const qty = parseFloat(item.quantity) || 0;
        const hours = parseFloat(item.hours) || 0;
        const price = parseFloat(item.unit_price) || 0;
        const computed = hours > 0 ? qty * hours * price : qty * price;
        return {
          ...item,
          quantity: qty,
          hours,
          unit_price: price,
          amount: item.amount_overridden ? (parseFloat(item.amount) || 0) : computed
        };
      });
      const subtotal = recalcItems.reduce((sum, i) => sum + i.amount, 0);
      const gst = +(subtotal * (gstRate || 0) / 100).toFixed(2);
      const total = +(subtotal + gst).toFixed(2);

      const updateData = {
        ...formData,
        shipping_info: cleanShipping(shipping),
        document_number: documentNumber,
        line_items: recalcItems,
        subtotal,
        gst_amount: gst,
        gst_rate: gstRate || 0,
        total_amount: total
      };

      await axios.put(`${BACKEND_URL}/api/invoices/${id}`, updateData);
      toast.success('Invoice updated successfully');
      navigate(`/admin/billing/invoice/${id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update invoice'));
    } finally {
      setSaving(false);
    }
  };

  const { subtotal, gst, total } = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/admin/billing/invoice/${id}`)}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Invoice</h1>
              <p className="text-gray-600 mt-1">Modify invoice details and pricing</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Document Number */}
              <Card>
                <CardHeader>
                  <CardTitle>Document Number</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-w-xs space-y-2">
                    <Label>Invoice Number</Label>
                    <Input
                      data-testid="edit-inv-number-input"
                      value={documentNumber}
                      onChange={(e) => setDocumentNumber(e.target.value)}
                    />
                    <p className="text-xs text-gray-500">Change carefully — duplicates are rejected automatically.</p>
                  </div>
                </CardContent>
              </Card>
              {/* Covered Delivery Orders (consolidated invoices) */}
              {coveredDOs.length > 0 && (
                <Card className="border-emerald-200 bg-emerald-50/40" data-testid="covered-dos-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-emerald-800">
                      Covered Delivery Orders ({coveredDOs.length})
                    </CardTitle>
                    <p className="text-xs text-gray-500">Cross-check the DOs consolidated into this invoice. Click a DO to open it in a new tab.</p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 border-b">
                            <th className="py-1.5 pr-3">DO Number</th>
                            <th className="py-1.5 pr-3">Date</th>
                            <th className="py-1.5 pr-3">Lorry</th>
                            <th className="py-1.5 pr-3">Address</th>
                            <th className="py-1.5 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {coveredDOs.map((d) => (
                            <tr key={d.id} className="border-b border-emerald-100 last:border-0">
                              <td className="py-1.5 pr-3">
                                <a
                                  href={`/admin/billing/delivery-order/${d.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-medium text-emerald-700 hover:underline"
                                  data-testid={`covered-do-link-${d.document_number}`}
                                >
                                  {d.document_number}
                                </a>
                              </td>
                              <td className="py-1.5 pr-3 whitespace-nowrap">
                                {d.delivery_date ? new Date(d.delivery_date).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                              </td>
                              <td className="py-1.5 pr-3">{d.lorry_no || '-'}</td>
                              <td className="py-1.5 pr-3 text-xs text-gray-600 max-w-[220px] truncate">{d.delivery_address || '-'}</td>
                              <td className="py-1.5 text-right font-semibold whitespace-nowrap">${(d.amount || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={4} className="py-1.5 text-right text-xs font-semibold text-gray-600">DO total</td>
                            <td className="py-1.5 text-right font-bold text-emerald-800">
                              ${coveredDOs.reduce((s, d) => s + (d.amount || 0), 0).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Customer Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Name *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        required
                        data-testid="edit-customer-name"
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                        required
                        data-testid="edit-customer-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      required
                      data-testid="edit-customer-email"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={formData.customer_address}
                      onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                      rows={2}
                      data-testid="edit-customer-address"
                    />
                  </div>
                  <div>
                    <Label>Customer P/O Number</Label>
                    <Input
                      value={formData.po_number}
                      onChange={(e) => setFormData({...formData, po_number: e.target.value})}
                      placeholder="Purchase Order number (optional)"
                      data-testid="edit-po-number"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Line Items */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Line Items</CardTitle>
                    <Button type="button" onClick={addLineItem} variant="outline" size="sm" data-testid="add-line-item-btn">
                      <Plus size={16} className="mr-1" />
                      Add Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <RateCardLibrary onAdd={addRateFromCard} />
                  <ChargeLibrary onAdd={addChargeFromLibrary} />
                  <div className="space-y-4">
                    {formData.line_items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end p-4 bg-gray-50 rounded-lg">
                        <div className="col-span-4">
                          <Label>Description</Label>
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            placeholder="Service description"
                            data-testid={`edit-line-item-description-${index}`}
                          />
                          <Input
                            value={item.do_number || ''}
                            onChange={(e) => updateLineItem(index, 'do_number', e.target.value)}
                            placeholder="DO No. e.g. DO-131"
                            className="mt-1 h-8 text-xs"
                            data-testid={`edit-line-item-do-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Lorry No.</Label>
                          <Input
                            value={item.lorry_no || ''}
                            onChange={(e) => updateLineItem(index, 'lorry_no', e.target.value)}
                            placeholder="XD1234A"
                            data-testid={`edit-line-item-lorry-${index}`}
                          />
                          <Input
                            value={item.uom || ''}
                            onChange={(e) => updateLineItem(index, 'uom', e.target.value)}
                            placeholder="UOM e.g. 20' / TRK"
                            className="mt-1 h-8 text-xs"
                            data-testid={`edit-line-item-uom-${index}`}
                          />
                        </div>
                        <div className="col-span-1">
                          <Label>Qty</Label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            data-testid={`edit-line-item-qty-${index}`}
                          />
                        </div>
                        <div className="col-span-1">
                          <Label>Hours</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            value={item.hours || ''}
                            onChange={(e) => updateLineItem(index, 'hours', e.target.value)}
                            placeholder="0"
                            data-testid={`edit-line-item-hours-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Unit Price ($)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                            data-testid={`edit-line-item-price-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Amount ($)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.amount}
                            onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                            data-testid={`edit-line-item-amount-${index}`}
                          />
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`remove-line-item-${index}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {formData.line_items.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No items added. Click "Add Item" to add line items.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Additional Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Additional Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Invoice Date (Job Request Date)</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                      data-testid="edit-invoice-date"
                    />
                    <p className="text-xs text-gray-500 mt-1">Date when the customer requested the job</p>
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Input
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                      placeholder="COD - Cash on Delivery"
                      data-testid="edit-payment-terms"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default: COD - Cash on Delivery. Editable to any term (Net 7, Net 30, etc.)</p>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={3}
                      placeholder="Additional notes for the invoice..."
                      data-testid="edit-notes"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Amount Paid ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount_paid}
                        onChange={(e) => setFormData({...formData, amount_paid: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                        data-testid="edit-amount-paid"
                      />
                      <p className="text-xs text-gray-500 mt-1">For partial payments</p>
                    </div>
                    <div>
                      <Label>Balance Due ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.balance_due}
                        onChange={(e) => setFormData({...formData, balance_due: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                        data-testid="edit-balance-due"
                      />
                      <p className="text-xs text-gray-500 mt-1">Outstanding amount</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <ShippingInfoCard value={shipping} onChange={setShipping} />
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-32">
                <CardHeader>
                  <CardTitle>Invoice Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">GST ({gstRate || 0}%):</span>
                      <span className="font-medium">${gst.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between text-lg">
                        <span className="font-bold">Total:</span>
                        <span className="font-bold text-green-600">${total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <Button
                      type="submit"
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      disabled={saving || formData.line_items.length === 0}
                      data-testid="save-invoice-btn"
                    >
                      {saving ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save size={20} />
                          Save Changes
                        </span>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate(`/admin/billing/invoice/${id}`)}
                      data-testid="cancel-edit-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
